import React, {PureComponent} from 'react';
import withStyles from '@mui/styles/withStyles';
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import {ExerciseType} from "../../../core/models/ExerciseType";
import AsyncLineGraph from "../../graphs/AsyncLineGraph";
import AsyncBarGraph from "../../graphs/AsyncBarGraph";

const useStyles = theme => ({
	tabBar: {
		flexGrow: 1,
	},
	media: {
		height: 225,
	},
});

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`full-width-tabpanel-${index}`}
			{...other}
		>
			{value === index ? children : null}
		</div>
	);
}

class RoutineExerciseDetailBloopGraphsTab extends PureComponent {
	/**
	 * @param props Containing:
	 *      'exercise': The exercise you want to modify.
	 *      'isShowing': Is it showing?
	 *      'updateSwipeableViewsHeightFunc': Update parent height of view.
	 */
	constructor(props) {
		super(props);

		this.state = {
			exercise: this.props.exercise,
			value: UserLocalStorage.get('routineChartTabChoice') ?? 1,
			hasEverBeenShown: false,
			isShowing: false,
		};
	}

	componentWillReceiveProps(nextProps, nextContext) {
		this.setState({
			exercise: nextProps.exercise,
			hasEverBeenShown: !this.state.hasEverBeenShown || nextProps.exercise.routineExerciseId !== this.state.exercise.routineExerciseId ? nextProps.isShowing : true,
			isShowing: nextProps.isShowing,
		});
	}

	shouldComponentUpdate (nextProps) {
		return nextProps.isShowing || nextProps.exercise.routineExerciseId !== this.state.exercise.routineExerciseId;
	};

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.props.updateSwipeableViewsHeightFunc();
	}

	handleChange = (event, newValue) => {
		this.setState({
			value: newValue
		});
		UserLocalStorage.set('routineChartTabChoice', newValue);
	};

	hasLogs = () => {
		return this.state.exercise.logGroups && this.state.exercise.logGroups.length > 0;
	}

	renderPerLogGraphs = () => {
		if (!this.state.hasEverBeenShown || this.state.value !== 0) {
			return <div/>;
		}

		if (!this.hasLogs()) {
			return this.renderEmptyState();
		}

		let logs = this.state.exercise.logGroups.map(group => group.logs).flat();
		logs = logs.reverse();

		let dates = logs.map(log => (new Date(log.dateCreated)).toLocaleDateString());

		let exerciseType = this.state.exercise.type;

		if (exerciseType.id === ExerciseType.WeightAndReps) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Reps', dates, () => logs.map(log => log.values[1]))}
					{this.renderLineGraph('Weight', dates, () => logs.map(log => log.values[0]))}
					{this.renderLineGraph('Volume', dates, () => logs.map(log => log.multiplied))}
					{this.renderBarGraph(
						'Max Reps Per Weight',
						() => {
							return this.processLogsForBarGraph(logs);
						})
					}
					{this.renderLineGraph('1RM (Brzycki formula)', dates, () => logs.map(log => Math.round(log.values[0] / ( 1.0278-(0.0278*log.values[1])))))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.Reps) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Reps', dates, () => logs.map(log => log.values[0]))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.DistanceAndTime) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Distance', dates, () => logs.map(log => log.values[0]))}
					{this.renderLineGraph('Time', dates, () => logs.map(log => log.values[1]))}
					{this.renderLineGraph('Average Speed', dates, () => logs.map(log => log.divided))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.Time) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Time', dates, () => logs.map(log => log.values[0]))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.WeightAndTime) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Weight', dates, () => logs.map(log => log.values[0]))}
					{this.renderLineGraph('Time', dates, () => logs.map(log => log.values[1]))}
				</Box>
			);
		}

		return <React.Fragment/>
	};

	renderPerDay = () => {
		if (!this.state.hasEverBeenShown || this.state.value !== 1) {
			return <div/>;
		}

		if (!this.hasLogs()) {
			return this.renderEmptyState();
		}

		let logGroups = [...this.state.exercise.logGroups].reverse();

		let dates = logGroups.map(logGroup => (new Date(logGroup.date)).toLocaleDateString());

		let exerciseType = this.state.exercise.type;

		if (exerciseType.id === ExerciseType.WeightAndReps) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Max Reps', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topRepsLogIdx].values[1]))}
					{this.renderLineGraph('Max Weight', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topWeightLogIdx].values[0]))}
					{this.renderLineGraph('Max Volume', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topVolumeLogIdx].multiplied))}
					{this.renderLineGraph('Total Volume', dates, () => logGroups.map(logGroup => logGroup.totalVolume))}
					{this.renderBarGraph(
						'Max Reps Per Weight',
						() => {
							let logs = logGroups.map(group => group.logs).flat();

							return this.processLogsForBarGraph(logs);
						}
					)}
					{this.renderLineGraph(
						'Max 1RM (Brzycki formula)',
						dates,
						() => {
							return logGroups.map(logGroup => {
								let log1RMs = logGroup.logs.map(log => Math.round(log.values[0] / ( 1.0278-(0.0278*log.values[1]))));

								return Math.max(...log1RMs);
							});
						}
					)}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.Reps) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Max Reps', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topRepsLogIdx].values[0]))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.DistanceAndTime) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Max Distance', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topDistanceLogIdx].values[0]))}
					{this.renderLineGraph('Max Time', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topTimeLogIdx].values[1]))}
					{this.renderLineGraph('Max Average Speed', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topSpeedLogIdx].divided))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.Time) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Max Time', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topTimeLogIdx].values[0]))}
				</Box>
			);
		}

		if (exerciseType.id === ExerciseType.WeightAndTime) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.5 }}>
					{this.renderLineGraph('Max Weight', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topWeightLogIdx].values[0]))}
					{this.renderLineGraph('Max Time', dates, () => logGroups.map(logGroup => logGroup.logs[logGroup.topTimeLogIdx].values[1]))}
				</Box>
			);
		}

		return <React.Fragment/>
	}

	processLogsForBarGraph = (logs) => {
		let maxRepsPerWeight = new Map();
		let rpeValuesPerWeight = new Map();

		logs.forEach(log => {
			let weight = log.values[0];
			let reps = log.values[1];

			if (maxRepsPerWeight.has(weight)) {
				if (reps > maxRepsPerWeight.get(weight)) {
					maxRepsPerWeight.set(weight, reps);
					rpeValuesPerWeight.set(weight, log.rpeValue);
				} else if (reps === maxRepsPerWeight.get(weight)) {
					if (
						log.rpeValue &&
						(
							!rpeValuesPerWeight.get(weight) ||
							(log.rpeValue < rpeValuesPerWeight.get(weight))
						)
					) {
						rpeValuesPerWeight.set(weight, log.rpeValue);
					}
				}
			} else {
				maxRepsPerWeight.set(weight, reps);
				rpeValuesPerWeight.set(weight, log.rpeValue);
			}
		});

		let sortedWeights = Array.from(maxRepsPerWeight.keys()).sort((a, b) => a - b);
		let maxReps = sortedWeights.map(weight => maxRepsPerWeight.get(weight));
		let rpeValues = sortedWeights.map(weight => rpeValuesPerWeight.get(weight));

		return [sortedWeights, maxReps, rpeValues];
	}

	renderEmptyState = () => {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
				<Typography variant='caption' color='text.disabled'>
					Log some sets to see graphs
				</Typography>
			</Box>
		);
	}

	renderLineGraph = (label, dates, closure) => {
		return (
			<Paper variant="outlined" key={label} sx={{ p: 1, pt: 1.5 }}>
				<Typography variant='caption' sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary', pl: 1 }}>
					{label}
				</Typography>
				<AsyncLineGraph label={label} dates={dates} fetchData={closure} showLegend={false}/>
			</Paper>
		);
	}

	renderBarGraph = (label, fetchData) => {
		return (
			<Paper variant="outlined" key={label} sx={{ p: 1, pt: 1.5 }}>
				<Typography variant='caption' sx={{ fontWeight: 600, fontSize: 11, color: 'text.secondary', pl: 1 }}>
					{label}
				</Typography>
				<AsyncBarGraph label={label} fetchData={fetchData}/>
			</Paper>
		);
	}

	render() {
		const {classes} = this.props;

		return <React.Fragment>
			<Paper square className={classes.tabBar} elevation={0}>
				<Tabs
					variant="fullWidth"
					value={this.state.value}
					onChange={this.handleChange}
					indicatorColor="secondary"
					textColor="secondary"
					centered
					sx={{
						minHeight: 40,
						'& .MuiTab-root': {
							minHeight: 40,
							textTransform: 'none',
							fontSize: 13,
							fontWeight: 500,
						},
					}}
				>
					<Tab label="All Logs" />
					<Tab label="Per Day" />
				</Tabs>
			</Paper>

				<TabPanel value={this.state.value} index={0}>
					{this.renderPerLogGraphs()}
				</TabPanel>
				<TabPanel value={this.state.value} index={1}>
					{this.renderPerDay()}
				</TabPanel>
		</React.Fragment>
	}
}

export default withStyles(useStyles)(RoutineExerciseDetailBloopGraphsTab);
